/**
 * MLModelConfig - Configurazione dei modelli di machine learning
 * Definisce i parametri e le configurazioni per i vari modelli predittivi
 */

/**
 * Configurazioni per i modelli predittivi
 */
export const modelConfigs = {
    /**
     * Configurazione per il modello di predizione del recupero
     */
    recoveryPredictor: {
      // Parametri del modello
      params: {
        learningRate: 0.01,
        epochs: 50,
        batchSize: 32,
        validationSplit: 0.2
      },
      // Features da utilizzare per il training
      features: [
        'totalVolume',
        'avgIntensity',
        'setCount',
        'muscleGroup',
        'exerciseType',
        'prevRecoveryTime'
      ],
      // Struttura della rete neurale (se applicabile)
      layers: [
        { units: 16, activation: 'relu' },
        { units: 8, activation: 'relu' },
        { units: 1, activation: 'linear' }
      ],
      // Soglie per i livelli di confidenza
      confidenceThresholds: {
        high: 0.8,
        medium: 0.6,
        low: 0.4
      }
    },
    
    /**
     * Configurazione per il modello di predizione dei plateau
     */
    plateauPredictor: {
      params: {
        learningRate: 0.005,
        epochs: 100,
        batchSize: 32,
        validationSplit: 0.2
      },
      features: [
        'volumeConsistency',
        'progressionSlope',
        'trainingFrequency',
        'exerciseType',
        'daysSinceLastProgress'
      ],
      layers: [
        { units: 32, activation: 'relu' },
        { units: 16, activation: 'relu' },
        { units: 8, activation: 'relu' },
        { units: 1, activation: 'sigmoid' }
      ],
      confidenceThresholds: {
        high: 0.85,
        medium: 0.7,
        low: 0.5
      }
    },
    
    /**
     * Configurazione per il modello di predizione della progressione
     */
    progressionPredictor: {
      params: {
        learningRate: 0.01,
        epochs: 75,
        batchSize: 32,
        validationSplit: 0.2
      },
      features: [
        'currentWeight',
        'volumeTrend',
        'intensityTrend',
        'restDays',
        'exerciseType',
        'muscleGroup',
        'experienceLevel'
      ],
      layers: [
        { units: 24, activation: 'relu' },
        { units: 12, activation: 'relu' },
        { units: 1, activation: 'linear' }
      ],
      confidenceThresholds: {
        high: 0.8,
        medium: 0.65,
        low: 0.5
      }
    },
    
    /**
     * Configurazione per il modello di ottimizzazione del volume
     */
    volumeOptimizer: {
      params: {
        learningRate: 0.008,
        epochs: 60,
        batchSize: 32,
        validationSplit: 0.2
      },
      features: [
        'muscleGroup',
        'lastSessionVolume',
        'lastSessionIntensity',
        'recoveryStatus',
        'progressionRate',
        'experienceLevel'
      ],
      layers: [
        { units: 16, activation: 'relu' },
        { units: 8, activation: 'relu' },
        { units: 1, activation: 'linear' }
      ],
      confidenceThresholds: {
        high: 0.85,
        medium: 0.7,
        low: 0.5
      }
    }
  };
  
  /**
   * Raggruppamenti predefiniti di muscoli per l'analisi
   */
  export const muscleGroupMappings = {
    // Mappatura dettagliata -> raggruppamento generale
    'Pettorali': 'Petto',
    'Petto': 'Petto',
    'Chest': 'Petto',
    
    'Dorsali': 'Schiena',
    'Schiena': 'Schiena',
    'Trapezi': 'Schiena',
    'Back': 'Schiena',
    
    'Quadricipiti': 'Gambe',
    'Femorali': 'Gambe',
    'Polpacci': 'Gambe',
    'Gambe': 'Gambe',
    'Legs': 'Gambe',
    
    'Deltoidi': 'Spalle',
    'Spalle': 'Spalle',
    'Shoulders': 'Spalle',
    
    'Bicipiti': 'Braccia',
    'Tricipiti': 'Braccia',
    'Avambracci': 'Braccia',
    'Arms': 'Braccia',
    
    'Addominali': 'Core',
    'Obliqui': 'Core',
    'Lombari': 'Core',
    'Core': 'Core'
  };
  
  /**
   * Classifica gli esercizi per tipo
   */
  export const exerciseTypeMappings = {
    // Mappatura di esercizi -> tipo
    // Esercizi composti
    'Squat': 'compound',
    'Stacco': 'compound',
    'Deadlift': 'compound',
    'Panca': 'compound',
    'Bench Press': 'compound',
    'Military Press': 'compound',
    'Trazioni': 'compound',
    'Pull-up': 'compound',
    'Chin-up': 'compound',
    'Dip': 'compound',
    'Affondi': 'compound',
    'Lunge': 'compound',
    'Rematore': 'compound',
    'Row': 'compound',
    
    // Esercizi di isolamento
    'Curl': 'isolation',
    'Extension': 'isolation',
    'Fly': 'isolation',
    'Croci': 'isolation',
    'Alzate': 'isolation',
    'Lateral Raise': 'isolation',
    'Calf Raise': 'isolation',
    'Kick-back': 'isolation',
    'Crunch': 'isolation',
    
    // Esercizi isometrici
    'Plank': 'isometric',
    'Wall Sit': 'isometric',
    'L-Sit': 'isometric',
    'Front Lever': 'isometric',
    
    // Esercizi a corpo libero
    'Push-up': 'bodyweight',
    'Piegamenti': 'bodyweight',
    'Sit-up': 'bodyweight',
    'Addominali': 'bodyweight',
    'Burpee': 'bodyweight'
  };
  
  /**
   * Ottieni il tipo di esercizio in base al nome
   * @param {string} exerciseName - Nome dell'esercizio
   * @returns {string} Tipo di esercizio (compound, isolation, isometric, bodyweight)
   */
  export function getExerciseType(exerciseName) {
    if (!exerciseName) return 'unknown';
    
    const name = exerciseName.toLowerCase();
    
    // Cerca corrispondenze nelle mappature
    for (const [key, type] of Object.entries(exerciseTypeMappings)) {
      if (name.includes(key.toLowerCase())) {
        return type;
      }
    }
    
    // Classificazione in base a parole chiave
    if (name.includes('press') || 
        name.includes('squat') || 
        name.includes('dead') || 
        name.includes('row')) {
      return 'compound';
    }
    
    if (name.includes('curl') || 
        name.includes('raise') || 
        name.includes('fly') || 
        name.includes('extension')) {
      return 'isolation';
    }
    
    if (name.includes('hold') || 
        name.includes('plank') || 
        name.includes('isometric')) {
      return 'isometric';
    }
    
    return 'unknown';
  }
  
  /**
   * Standardizza il nome del gruppo muscolare
   * @param {string} muscleGroup - Nome del gruppo muscolare
   * @returns {string} Nome standardizzato
   */
  export function standardizeMuscleGroup(muscleGroup) {
    if (!muscleGroup) return 'Altro';
    
    // Cerca corrispondenze nelle mappature
    for (const [key, standardName] of Object.entries(muscleGroupMappings)) {
      if (muscleGroup.toLowerCase().includes(key.toLowerCase())) {
        return standardName;
      }
    }
    
    return muscleGroup;
  }
  
  /**
   * Parametri di default per il ML in base al livello di esperienza
   */
  export const defaultMLParams = {
    beginner: {
      // Principianti progrediscono più velocemente e recuperano più rapidamente
      recoverySpeedMultiplier: 0.8,
      progressionRateMultiplier: 1.5,
      volumeToleranceBase: 6,
      intensityToleranceBase: 4,
      plateauThreshold: 4 // Settimane senza progresso prima di considerarlo plateau
    },
    intermediate: {
      // Parametri bilanciati per intermedi
      recoverySpeedMultiplier: 1.0,
      progressionRateMultiplier: 1.0,
      volumeToleranceBase: 7,
      intensityToleranceBase: 6,
      plateauThreshold: 3 // Settimane senza progresso prima di considerarlo plateau
    },
    advanced: {
      // Avanzati progrediscono più lentamente e necessitano più recupero
      recoverySpeedMultiplier: 1.2,
      progressionRateMultiplier: 0.7,
      volumeToleranceBase: 8,
      intensityToleranceBase: 8,
      plateauThreshold: 2 // Settimane senza progresso prima di considerarlo plateau
    }
  };
  
  /**
   * Ottiene i parametri ML in base al livello di esperienza
   * @param {string} experienceLevel - Livello di esperienza (beginner, intermediate, advanced)
   * @returns {Object} Parametri ML
   */
  export function getMLParamsByExperience(experienceLevel = 'intermediate') {
    return defaultMLParams[experienceLevel] || defaultMLParams.intermediate;
  }